import * as React from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { filterBy } from "@progress/kendo-data-query";
import { useNominalsBySubType, useAllNominals } from "services/nominalsService";
import { Grid, GridColumn as Column, GridNoRecords } from "@progress/kendo-react-grid";
import { ComboBox } from "@progress/kendo-react-dropdowns";
import { Form, FormElement, Field } from "@progress/kendo-react-form";
import * as api from "../../api/index";
import { AmountFormat, AmountFormatCell, DateFormatCell } from "components/common/formatter";
import { useState, useEffect, useRef } from "react";
import SearchComboBox from "components/common/SearchComboBox";
import CONSTANT from "CONSTANT/Matter/ComboxType";
import { NameTextCell } from "components/common/GridComponents";
import { Checkbox, NumericTextBox } from "@progress/kendo-react-inputs";
import { useActiveVats } from "services/vatService";
import { Button } from "@progress/kendo-react-buttons";
import styles from "./Bills.module.scss";
import { GridLoader } from "components/GridLoader";
import { LuTag } from "react-icons/lu";
import { IoPersonOutline } from "react-icons/io5";
import { LiaCoinsSolid } from "react-icons/lia";
import { FaRegBuilding } from "react-icons/fa";
import { useCompany } from "services/companyService";
import { useActiveFeeEarner } from "services/userService";

const findNominalByRef = (list, ref) => {
  if (ref == null || ref === "") return null;
  const target = String(ref).trim();
  return (
    list?.find((item) => String(item.nominalRef).trim() === target) ||
    list?.find((item) => String(item.display || "").startsWith(target)) ||
    null
  );
};

const UpdateFieldCard = ({ icon: Icon, label, children, className = "" }) => (
  <div className={`${styles["section-card"]} ${className}`}>
    <div className="d-flex align-items-center gap-2 mb-2">
      <span
        className="d-flex align-items-center justify-content-center flex-shrink-0 rounded"
        style={{ width: 28, height: 28, background: "#ECEBFF" }}
      >
        {Icon && <Icon size={14} color="#162EFF" />}
      </span>
      <span className="fw-semibold fs-6">{label}</span>
    </div>
    {children}
  </div>
);

const ValidatedComboBox = ({
  label,
  icon,
  value,
  isRequired = true,
  showValidation,
  textField,
  ...comboBoxProps
}) => {
  const getValidationValue = () => {
    if (textField === "display") {
      return value?.nominalRef;
    }
    return value;
  };

  const hasError = showValidation && isRequired && !getValidationValue();

  return (
    <UpdateFieldCard icon={icon} label={label}>
      <ComboBox
        {...comboBoxProps}
        textField={textField}
        style={{
          width: "100%",
          border: hasError ? "1px solid red" : "",
          borderRadius: "4px",
          ...comboBoxProps.style
        }}
        value={value}
      />
    </UpdateFieldCard>
  );
};

const truncateToTwoDecimals = (value) => {
  return Math.trunc((value || 0) * 100) / 100;
};

const roundToTwoDecimals = (value) => {
  return parseFloat((value || 0).toFixed(2));
};

const splitEditHandlersRef = { current: {} };

const ChangeToCell = (cellProps) => (
  <td>
    <NumericTextBox
      value={cellProps.dataItem.changeTo}
      onChange={(e) =>
        splitEditHandlersRef.current.handleChangeToEdit?.(
          cellProps.dataItem.billFeeEarnerRef,
          e.value || 0
        )
      }
      format="n2"
      spinners={false}
      min={0}
      className={styles["numeric-input"]}
      style={{ width: "100%" }}
    />
  </td>
);

const PercentSplitCell = (cellProps) => (
  <td>
    <NumericTextBox
      value={cellProps.dataItem.percentSplit}
      onChange={(e) =>
        splitEditHandlersRef.current.handlePercentSplitEdit?.(
          cellProps.dataItem.billFeeEarnerRef,
          e.value || 0
        )
      }
      format="n2"
      spinners={false}
      min={0}
      max={100}
      className={styles["numeric-input"]}
      style={{ width: "100%" }}
    />
  </td>
);

const PercentSplitDialog = ({ selectedRecords, totalBillNet, onCancel, onUpdate }) => {
  const buildInitialRows = () => {
    const groups = {};
    selectedRecords.forEach((item) => {
      const key = item.billFeeEarnerRef || "—";
      const netAmount = typeof item.billedNetAmount === "number" ? item.billedNetAmount : 0;
      if (!groups[key]) {
        groups[key] = { billFeeEarnerRef: key, value: 0, billNet: 0 };
      }
      groups[key].value += item.value || 0;
      groups[key].billNet += netAmount;
    });

    const initialRows = Object.values(groups).map((group) => ({
      billFeeEarnerRef: group.billFeeEarnerRef,
      value: roundToTwoDecimals(group.value),
      billNet: roundToTwoDecimals(group.billNet),
      changeTo: roundToTwoDecimals(group.billNet),
      percentSplit:
        totalBillNet > 0 ? truncateToTwoDecimals((group.billNet / totalBillNet) * 100) : 0,
    }));

    if (initialRows.length > 1) {
      const lastIndex = initialRows.length - 1;
      const sumOthersPercent = initialRows.reduce(
        (sum, row, idx) => (idx === lastIndex ? sum : sum + row.percentSplit),
        0
      );
      initialRows[lastIndex] = {
        ...initialRows[lastIndex],
        percentSplit: roundToTwoDecimals(100 - sumOthersPercent),
      };
    }

    return initialRows;
  };

  const [rows, setRows] = useState(buildInitialRows);
  const [validationError, setValidationError] = useState("");

  const rebalancePercentSplits = (updatedRows, editedFeeEarnerRef) => {
    if (updatedRows.length < 2) return updatedRows;

    const totalChangeTo = updatedRows.reduce((sum, row) => sum + (row.changeTo || 0), 0);
    if (Math.abs(totalChangeTo - totalBillNet) >= 0.01) return updatedRows;

    let balancingIndex = -1;
    for (let i = updatedRows.length - 1; i >= 0; i--) {
      if (updatedRows[i].billFeeEarnerRef !== editedFeeEarnerRef) {
        balancingIndex = i;
        break;
      }
    }
    if (balancingIndex === -1) return updatedRows;

    const sumOthersPercent = updatedRows.reduce(
      (sum, row, idx) => (idx === balancingIndex ? sum : sum + (row.percentSplit || 0)),
      0
    );
    return updatedRows.map((row, idx) =>
      idx === balancingIndex
        ? { ...row, percentSplit: roundToTwoDecimals(100 - sumOthersPercent) }
        : row
    );
  };

  const handleChangeToEdit = (rowFeeEarnerRef, newValue) => {
    setValidationError("");
    setRows((prevRows) =>
      rebalancePercentSplits(
        prevRows.map((row) => {
          if (row.billFeeEarnerRef !== rowFeeEarnerRef) return row;
          const percentSplit =
            totalBillNet > 0 ? truncateToTwoDecimals(((newValue || 0) / totalBillNet) * 100) : 0;
          return { ...row, changeTo: roundToTwoDecimals(newValue), percentSplit };
        }),
        rowFeeEarnerRef
      )
    );
  };

  const handlePercentSplitEdit = (rowFeeEarnerRef, newPercent) => {
    setValidationError("");
    setRows((prevRows) =>
      rebalancePercentSplits(
        prevRows.map((row) => {
          if (row.billFeeEarnerRef !== rowFeeEarnerRef) return row;
          const changeTo = roundToTwoDecimals((totalBillNet * (newPercent || 0)) / 100);
          return { ...row, percentSplit: roundToTwoDecimals(newPercent), changeTo };
        }),
        rowFeeEarnerRef
      )
    );
  };

  splitEditHandlersRef.current.handleChangeToEdit = handleChangeToEdit;
  splitEditHandlersRef.current.handlePercentSplitEdit = handlePercentSplitEdit;

  const totals = rows.reduce(
    (acc, row) => ({
      value: acc.value + (row.value || 0),
      billNet: acc.billNet + (row.billNet || 0),
      changeTo: acc.changeTo + (row.changeTo || 0),
      percentSplit: acc.percentSplit + (row.percentSplit || 0),
    }),
    { value: 0, billNet: 0, changeTo: 0, percentSplit: 0 }
  );

  const SplitTotalLabelFooterCell = () => (
    <td className="fw-bold">Totals</td>
  );

  const splitAmountFooterCell = (field) => () => (
    <td className="text-end fw-bold">
      {AmountFormat(totals[field])}{field === "percentSplit" ? " %" : ""}
    </td>
  );

  const handleUpdate = () => {
    const isChangeToValid = Math.abs(totals.changeTo - totalBillNet) < 0.01;
    const isPercentValid = Math.abs(totals.percentSplit - 100) < 0.01;

    if (!isChangeToValid || !isPercentValid) {
      setValidationError(
        !isChangeToValid
          ? `Total Change To (${totals.changeTo.toFixed(2)}) must equal Total Bill Net (${totalBillNet.toFixed(2)})`
          : `Total % Split must equal 100% (currently ${totals.percentSplit.toFixed(2)}%)`
      );
      return;
    }

    onUpdate(rows);
  };

  return (
    <Dialog title="Split" onClose={onCancel} className="dialog-xs" width={600}>
      <div className="p-3">
        <Grid data={rows} dataItemKey="billFeeEarnerRef" className="grid-header-sm">
          <Column
            field="billFeeEarnerRef"
            title="Bill FE"
            width="50"
            footerCell={SplitTotalLabelFooterCell}
          />
          <Column
            field="value"
            title="Value"
            className="grid-header-center"
            width="60"
            cell={(cellProps) => (
              <td style={{ textAlign: "center" }}>{AmountFormat(cellProps.dataItem.value || 0)}</td>
            )}
            footerCell={splitAmountFooterCell("value")}
          />
          <Column
            field="billNet"
            title="Bill Net"
            className="grid-header-center"
            width="60"
            cell={(cellProps) => (
              <td style={{ textAlign: "center" }}>{AmountFormat(cellProps.dataItem.billNet || 0)}</td>
            )}
            footerCell={splitAmountFooterCell("billNet")}
          />
          <Column
            field="changeTo"
            title="Change To"
            width="80"
            cell={ChangeToCell}
            footerCell={splitAmountFooterCell("changeTo")}
          />
          <Column
            field="percentSplit"
            title="% Split"
            width="80"
            cell={PercentSplitCell}
            footerCell={splitAmountFooterCell("percentSplit")}
          />
        </Grid>

        {validationError && (
          <div className="mt-3">
            <i style={{ color: "red" }}>{validationError}</i>
          </div>
        )}
      </div>

      <DialogActionsBar>
        <div className="dialog-footer">
          <Button
            className="common-btn bgBtnSecondary border-0 me-3"
            onClick={onCancel}
          >
            Cancel
          </Button>
          <Button className="common-btn me-4" onClick={handleUpdate}>
            Update
          </Button>
        </div>
      </DialogActionsBar>
    </Dialog>
  );
};

const MattersTimeRecords = (props) => {
  const [dataAvailable, setDataAvailable] = useState(false);
  const [error, setError] = useState(undefined);
  const [error1, setError1] = useState(undefined);
  const [nominal, setnominal] = useState();
  const [nominaldata, setnominaldata] = React.useState();
  const [data, setData] = React.useState([]);
  const [matters, setMatters] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [vatvalue, setvatvalue] = useState();
  const [vatsdata, setvatsData] = React.useState();
  const [showValidation, setShowValidation] = useState(false);
  const [selectedFeeEarner, setSelectedFeeEarner] = useState(null);
  const [showSplitDialog, setShowSplitDialog] = useState(false);

  const [summaryUnits, setSummaryUnits] = useState(0);
  const [summaryValue, setSummaryValue] = useState(0);
  const [summaryBillTo, setSummaryBillTo] = useState(0);

  const hasInitializedVat = useRef(false);

  const { data: activevatsdata = [] } = useActiveVats();
  const { data: company } = useCompany();
  const { data: activeFeeEarners = [] } = useActiveFeeEarner();
  const defaultProfitNominalRef =
    company?.defaultProfitCostNominalType === "User"
      ? activeFeeEarners.find(
        (fe) => fe.userRef === props.defaultValues?.matterFeeEarner,
      )?.defaultNominal
      : props.defaultValues?.defaultProfitNominal;

  const getSelectedRecordsSummary = () => {
    const selectedRecords = data.filter(item => item.add === true);
    const count = selectedRecords.length;
    const totalUnits = selectedRecords.reduce((sum, item) => sum + (item.units || 0), 0);
    const totalValue = selectedRecords.reduce((sum, item) => sum + (item.value || 0), 0);
    const totalBillVat = selectedRecords.reduce((sum, item) => sum + (item.billVat || 0), 0);
    const totalBillNet = selectedRecords.reduce((sum, item) => {
      const net = item.billedNetAmount;
      return typeof net === "number" ? sum + net : sum;
    }, 0);
    const totalBill = totalBillNet + totalBillVat;

    return { count, totalUnits, totalValue, totalBillVat, totalBillNet, totalBill };
  };

  useEffect(() => {
    const selectedRecords = data.filter(item => item.add === true);
    const totalUnits = selectedRecords.reduce((sum, item) => sum + (item.units || 0), 0);
    const totalValue = selectedRecords.reduce((sum, item) => sum + (item.value || 0), 0);
    const totalBillVat = selectedRecords.reduce((sum, item) => sum + (item.billVat || 0), 0);

    setSummaryUnits(totalUnits);
    setSummaryValue(totalValue);
    setSummaryBillTo(totalValue);
  }, [data]);

  const getOverallTotals = () => {
    const totalUnits = data.reduce((sum, item) => sum + (item.units || 0), 0);
    const totalValue = data.reduce((sum, item) => sum + (item.value || 0), 0);

    return { totalUnits, totalValue };
  };


  const AmountNameHeader = (props) => {
    return (
      <div style={{ textAlign: "center" }}>
        {props.title}
      </div>
    );
  };

  const TotalFooterCell = (props) => {
    return (isLoading ? null :
      <td style={{
        textAlign: "center",
        fontWeight: "bold",
        backgroundColor: "#f8f9fa",
        borderTop: "1px solid #a1baff",
        borderBottom: "none",
      }}>
        {props.children}
      </td>
    );
  };

  const TotalLabelFooterCell = () => {
    return (isLoading ? null :
      <td style={{ fontWeight: "bold", backgroundColor: "#f8f9fa", borderTop: "1px solid #a1baff" }}>
        Total
      </td>
    )
  };

  const EmptyFooterCell = () => {
    return (isLoading ? null :
      <td style={{
        backgroundColor: "#f8f9fa",
        borderTop: "1px solid #a1baff"
      }}>
      </td>
    )
  };

  const BillNetCell = (props) => {
    const value = props.dataItem.billedNetAmount;

    if (value === "w/o" || value === null || value === undefined) {
      return (
        <td style={{ textAlign: "center" }}>
          w/o
        </td>
      );
    }

    return (
      <td style={{ textAlign: "center" }}>
        {typeof value === 'number' ? value.toFixed(2) : value}
      </td>
    );
  };

  const onChangenominal = (event) => {
    let selectedNominal = event.value;
    if (selectedNominal && selectedNominal.display) {
      const parts = selectedNominal.display.split(' - ');
      const firstPart = parts[0]?.trim();


      selectedNominal = {
        ...selectedNominal,
        display: firstPart
      };
    }

    setnominal(selectedNominal);

    // Clear error when user selects a nominal ref
    if (error === true) {
      setError(false);
    }

    // Only apply to currently selected records
    if (data.length > 0 && selectedNominal) {
      const updatedData = data.map((item) => {
        if (!item.add) return item; // Skip unselected records
        return { ...item, nominalRef: selectedNominal.nominalRef };
      });
      setData(updatedData);
    }
    // If nominal is cleared (null), don't change anything - keep existing values
  };

  const calculateVatForRecords = (records, vatCode, selectedOnly = false) => {
    if (!vatCode || !vatCode.rate) {
      return records.map((item) => {
        if (selectedOnly && !item.add) return item;
        return { ...item, billVat: 0 };
      });
    }
    const vatRate = vatCode.rate / 100;
    return records.map((item) => {
      if (selectedOnly && !item.add) return item;
      const netAmount = typeof item.billedNetAmount === 'number' ? item.billedNetAmount : item.value;
      return {
        ...item,
        billVat: parseFloat(((netAmount || 0) * vatRate).toFixed(2)),
      };
    });
  };

  const adjustVatPennyDifferences = (records) => {
    const selectedRecords = records.filter(item => item.add === true);

    if (selectedRecords.length === 0) return records;

    const vatGroups = {};
    selectedRecords.forEach(record => {

      let vatRate = 0;
      const netAmount = typeof record.billedNetAmount === 'number' ? record.billedNetAmount : record.value;
      if (netAmount && netAmount !== 0) {
        vatRate = (record.billVat || 0) / netAmount;
      }
      const vatRateKey = vatRate.toFixed(4);

      if (!vatGroups[vatRateKey]) {
        vatGroups[vatRateKey] = {
          rate: vatRate,
          records: []
        };
      }
      vatGroups[vatRateKey].records.push(record);
    });

    // Process each VAT group
    Object.keys(vatGroups).forEach(vatRateKey => {
      const group = vatGroups[vatRateKey].records;
      const vatRate = vatGroups[vatRateKey].rate;

      if (group.length === 0) return;


      // Step 1: Calculate sum of individual rounded VAT amounts
      const sumOfIndividualVat = group.reduce((sum, item) => {
        const itemVat = parseFloat((item.billVat || 0).toFixed(2));
        return sum + itemVat;
      }, 0);

      // Step 2: Calculate VAT on total billedNetAmount
      const totalValue = group.reduce((sum, item) => {
        const netAmount = typeof item.billedNetAmount === 'number' ? item.billedNetAmount : item.value;
        return sum + (netAmount || 0);
      }, 0);
      const vatOnTotal = parseFloat((totalValue * vatRate).toFixed(2));


      // Step 3: Calculate the difference
      const difference = parseFloat((sumOfIndividualVat - vatOnTotal).toFixed(2));

      // Step 4: Adjust the last record in the group if there's a difference
      if (difference !== 0 && group.length > 0) {
        const lastRecord = group[group.length - 1];
        const recordIndex = records.findIndex(r => r.timeId === lastRecord.timeId);

        if (recordIndex !== -1) {
          const oldVat = records[recordIndex].billVat;
          const newVat = parseFloat((oldVat - difference).toFixed(2));
          records[recordIndex] = {
            ...records[recordIndex],
            billVat: newVat
          };
        }
      }
    });

    return records;
  };

  const onChangevat = (event) => {
    let selectedVat = event.value;

    if (selectedVat && selectedVat.display) {
      const parts = selectedVat.display.split(' - ');
      const firstPart = parts[0]?.trim();
      const lastPart = parts[parts.length - 1]?.trim();

      selectedVat = {
        ...selectedVat,
        display: `${firstPart} - ${lastPart}`
      };
    }

    setvatvalue(selectedVat);

    if (selectedVat && data.length > 0) {
      const updatedData = calculateVatForRecords(data, selectedVat, true);
      setData(updatedData);
    }
  };

  const filterData = (filter) => {
    const datas = activevatsdata.slice();
    return filterBy(datas, filter);
  };

  const filterChange = (event) => {
    setvatsData(filterData(event.filter));
  };

  const filternominalData = (filter) => {
    const data = nominallist.slice();
    return filterBy(data, filter);
  };

  const filternominal = (event) => {
    setnominaldata(filternominalData(event.filter));
  };

  const { data: incomeNominals, loading: nominalsLoading } = useNominalsBySubType(
    "ProfitAndLoss_Income",
    { excludeArchived: true },
  );
  const { data: allNominals } = useAllNominals();
  const nominallist = incomeNominals || [];


  const handleSubmit = (values) => {
    setShowValidation(true);

    // Check if at least one record is selected
    const hasSelectedRecords = data.some(item => item.add === true);
    if (!hasSelectedRecords) {
      setError1(true);
      return;
    }

    // Check if any selected record doesn't have nominal ref
    const selectedRecords = data.filter(item => item.add === true);
    const hasRecordsWithoutNominal = selectedRecords.some(record => !record.nominalRef);

    if (hasRecordsWithoutNominal) {
      setError(true);
      return;
    }

    const adjustedData = adjustVatPennyDifferences([...data]);
    const dataToPost = adjustedData.filter(item => item.add === true);

    props.changeMatterList(dataToPost);
    props.addNominalVatInfo({
      nominalRef: nominal,
      vatCode: vatvalue,
      feeEarnerRef: values.feeEarner ? values.feeEarner.userRef : null,
    });

    props.cancelEdit();
  };

  const itemChange = (e) => {
    let newData = data.map((item) => {
      if (item.timeId === e.dataItem.timeId) {
        const updatedItem = { ...item, add: e.value };

        if (e.value) {
          if (vatvalue && vatvalue.rate) {
            const vatRate = vatvalue.rate / 100;
            // Use billedNetAmount if it's a number, otherwise use value
            const netAmount = typeof item.billedNetAmount === 'number' ? item.billedNetAmount : item.value;
            updatedItem.billVat = parseFloat(((netAmount || 0) * vatRate).toFixed(2));
          }

          if (nominal && nominal.nominalRef) {
            updatedItem.nominalRef = nominal.nominalRef;
          }

          if (selectedFeeEarner && selectedFeeEarner.userRef) {
            updatedItem.billFeeEarnerRef = selectedFeeEarner.userRef;
          }
        }

        return updatedItem;
      }
      return item;
    });
    setData(newData);

    // Clear error if there are no selected records without nominal ref
    if (error === true) {
      const selectedRecordsWithoutNominal = newData.filter(item => item.add === true && !item.nominalRef);
      if (selectedRecordsWithoutNominal.length === 0) {
        setError(false);
      }
    }
  };

  const addDefaultValues = () => {
    const defaultVat = activevatsdata.find(
      (item) => item.vatCodeId === props.defaultValues?.defaultVatCodeId
    );
    if (defaultVat) {
      let selectedVat = defaultVat;

      if (selectedVat && selectedVat.display) {
        const parts = selectedVat.display.split(' - ');
        const firstPart = parts[0]?.trim();
        const lastPart = parts[parts.length - 1]?.trim();

        selectedVat = {
          ...selectedVat,
          display: `${firstPart} - ${lastPart}`
        };
      }
      setvatvalue(selectedVat);
      setData((prevData) => {
        if (prevData.length > 0) {
          return calculateVatForRecords(prevData, defaultVat, true);
        }
        return prevData;
      });
    }
  };



  const fetchNominal = async () => {
    setnominaldata(incomeNominals?.slice());
    let nominal =
      findNominalByRef(incomeNominals, defaultProfitNominalRef) ||
      findNominalByRef(allNominals, defaultProfitNominalRef);

    if (!nominal && defaultProfitNominalRef) {
      nominal = {
        nominalRef: String(defaultProfitNominalRef),
        display: String(defaultProfitNominalRef),
      };
    }

    if (nominal && nominal.display) {
      const parts = nominal.display.split(" - ");
      const firstPart = parts[0]?.trim();

      nominal = {
        ...nominal,
        display: firstPart,
      };
      setnominaldata((prev) => {
        const base = incomeNominals?.length
          ? incomeNominals.slice()
          : prev || [];
        const exists = base.some(
          (item) => String(item.nominalRef) === String(nominal.nominalRef),
        );
        return exists ? base : [nominal, ...base];
      });
      setnominal(nominal);
    } else {
      setnominal(null);
    }
  };

  useEffect(() => {
    const getMatterTimeRecordsData = async () => {
      setIsLoading(true);
      try {
        const response = await api.getMatterTimeRecords(props.matterId);
        const allRecords = response.data?.data || [];

        // Filter out records that are already in the parent grid
        const existingTimeIds = (props.existingRecords || []).map(r => r.timeId);
        const filteredRecords = allRecords.filter(
          record => !existingTimeIds.includes(record.timeId)
        );

        setMatters(
          filteredRecords.map((record) => ({
            type: "Time",
            billVat: 0,
            add: false,
            ...record,
            originalNominalRef: record.nominalRef,
            nominalRef:
              company?.defaultProfitCostNominalType === "User"
                ? defaultProfitNominalRef || ""
                : defaultProfitNominalRef || record.nominalRef,
            billFeeEarnerRef: record.feeEarnerRef, // Default Bill FE to FE
            billedNetAmount: record.value, // Initialize Bill Net with value
          }))
        );
      } catch (error) {
        console.error("Error fetching matter time records:", error);
        setMatters([]);
      } finally {
        setIsLoading(false);
      }
    };

    getMatterTimeRecordsData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.matterId, defaultProfitNominalRef]);

  const handleClearSelection = () => {
    setData((prevData) =>
      prevData.map((item) => ({
        ...item,
        add: false
      }))
    );

    if (error === true) {
      setError(false);
    }
  };

  const handleSelectAllChange = (selectAll) => {
    if (selectAll) {
      setData((prevData) => {
        const updatedData = prevData.map((item) => {
          const updatedItem = { ...item, add: true };

          if (vatvalue && vatvalue.rate) {
            const vatRate = vatvalue.rate / 100;
            const netAmount = typeof item.billedNetAmount === "number" ? item.billedNetAmount : item.value;
            updatedItem.billVat = parseFloat(((netAmount || 0) * vatRate).toFixed(2));
          }

          if (nominal && nominal.nominalRef) {
            updatedItem.nominalRef = nominal.nominalRef;
          }

          if (selectedFeeEarner && selectedFeeEarner.userRef) {
            updatedItem.billFeeEarnerRef = selectedFeeEarner.userRef;
          }

          return updatedItem;
        });

        if (error === true) {
          const selectedRecordsWithoutNominal = updatedData.filter(item => item.add === true && !item.nominalRef);
          if (selectedRecordsWithoutNominal.length === 0) {
            setError(false);
          }
        }

        return updatedData;
      });
    } else {
      handleClearSelection();
    }
  };

  const SelectionHeaderCell = (headerProps) => (
    <div className="gap-2 d-flex">
      <span>{headerProps.title}</span>
      <Checkbox
        style={{
          width: "18px",
          height: "18px",
          boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
        }}
        onChange={(e) => handleSelectAllChange(e.value)}
        value={data.length > 0 && data.every((item) => item.add)}
        disabled={data.length === 0}
      />
    </div>
  );

  useEffect(() => {
    if (
      activevatsdata?.length > 0 &&
      props?.defaultValues?.defaultVatCodeId &&
      !vatvalue &&
      !hasInitializedVat.current
    ) {
      setvatsData(activevatsdata.slice());
      addDefaultValues();
      hasInitializedVat.current = true; // Mark as initialized
    }
  }, [activevatsdata]);

  useEffect(() => {
    fetchNominal();
  }, [incomeNominals, allNominals, defaultProfitNominalRef]);


  const requiredValidator = (value) => {
    return value ? undefined : "invalid"; // triggers k-invalid class for Fee Earner field
  };

  useEffect(() => {
    setTimeout(() => {
      if (
        matters[0] !== null &&
        matters[0] !== "LIST_EMPTY" &&
        matters[0] !== undefined &&
        dataAvailable === false
      ) {
        setData(matters);
        setDataAvailable(true);
      } else if (matters[0] === "LIST_EMPTY") {
        setData([]);
        setDataAvailable(false);
      }
    }, 0);
  }, [matters]);

  const summary = getSelectedRecordsSummary();
  const overallTotals = getOverallTotals();


  const handleWriteOff = () => {
    const updatedData = data.map((item) => {
      if (item.add === true) {
        return { ...item, billedNetAmount: "w/o", billVat: 0 };
      }
      return item;
    });
    setData(updatedData);
    setTimeout(() => setSummaryBillTo(0), 0);
  };

  const handleApplyBillTo = () => {
    const selectedRecords = data.filter(item => item.add === true);
    if (selectedRecords.length === 0) {
      return;
    }


    // Step 1: Calculate total original billedNetAmount (use originalBilledNetAmount if exists, else use value)
    const totalOriginalValue = selectedRecords.reduce((sum, item) => {
      const origValue = item.originalBilledNetAmount !== undefined ? item.originalBilledNetAmount : item.value;
      return sum + (origValue || 0);
    }, 0);

    // Step 2: Calculate proportional values for each selected record
    // If all records have zero value, distribute equally instead
    const isAllZero = totalOriginalValue === 0;
    const equalShare = isAllZero ? summaryBillTo / selectedRecords.length : 0;

    let updatedData = data.map((item) => {
      if (!item.add) return item;

      // Preserve original billedNetAmount on first application
      const origValue = item.originalBilledNetAmount !== undefined ? item.originalBilledNetAmount : item.value;

      let newBilledNetAmount;
      if (isAllZero) {
        // Distribute equally if all values are zero
        newBilledNetAmount = equalShare;
      } else {
        // Formula: (Original Value / Total Original Value) × "Bill To Amount"
        const proportion = (origValue || 0) / totalOriginalValue;
        newBilledNetAmount = proportion * summaryBillTo;
      }

      const roundedNewBilledNetAmount = parseFloat(newBilledNetAmount.toFixed(2));


      return {
        ...item,
        billedNetAmount: roundedNewBilledNetAmount,           // Update the billedNetAmount field (not value)
        originalBilledNetAmount: origValue,         // Preserve original billedNetAmount
      };
    });

    // Step 3: Adjust penny difference in last selected record
    const updatedSelectedRecords = updatedData.filter(item => item.add === true);
    const sumOfRoundedValues = updatedSelectedRecords.reduce((sum, item) => sum + (item.billedNetAmount || 0), 0);
    const pennyDifference = parseFloat((summaryBillTo - sumOfRoundedValues).toFixed(2));

    if (pennyDifference !== 0 && updatedSelectedRecords.length > 0) {
      const lastRecord = updatedSelectedRecords[updatedSelectedRecords.length - 1];
      const lastRecordIndex = updatedData.findIndex(r => r.timeId === lastRecord.timeId);

      if (lastRecordIndex !== -1) {
        const oldBilledNetAmount = updatedData[lastRecordIndex].billedNetAmount;
        const newBilledNetAmount = parseFloat((oldBilledNetAmount + pennyDifference).toFixed(2));
        updatedData[lastRecordIndex] = {
          ...updatedData[lastRecordIndex],
          billedNetAmount: newBilledNetAmount
        };
      }
    }

    // Step 4: Recalculate VAT based on new billedNetAmount
    if (vatvalue && vatvalue.rate) {
      const vatRate = vatvalue.rate / 100;
      updatedData = updatedData.map((item) => {
        if (!item.add) return item;
        const newVat = parseFloat(((item.billedNetAmount || 0) * vatRate).toFixed(2));
        return {
          ...item,
          billVat: newVat
        };
      });
    }

    // Step 5: Apply VAT penny adjustment

    const finalData = adjustVatPennyDifferences(updatedData);

    setData(finalData);
    setTimeout(() => setSummaryBillTo(summaryBillTo), 0);

  };
  const getSelectedItemsCount = () => {
    return data.filter(item => item.add === true).length;
  };

  const handleApplyPercentSplit = (splitRows) => {
    const selectedRecords = data.filter(item => item.add === true);
    if (selectedRecords.length === 0) {
      return;
    }

    let updatedData = [...data];

    splitRows.forEach((group) => {
      const groupRecords = selectedRecords.filter(
        (item) => (item.billFeeEarnerRef || "—") === group.billFeeEarnerRef
      );
      if (groupRecords.length === 0) return;

      // Step 1: Calculate total original value for the fee earner group
      const totalOriginalValue = groupRecords.reduce((sum, item) => {
        const origValue = item.originalBilledNetAmount !== undefined ? item.originalBilledNetAmount : item.value;
        return sum + (typeof origValue === "number" ? origValue : 0);
      }, 0);

      // Step 2: Calculate proportional values for each record in the group
      const isAllZero = totalOriginalValue === 0;
      const equalShare = isAllZero ? group.changeTo / groupRecords.length : 0;

      const updatedGroupRecords = groupRecords.map((item) => {
        const origValue = item.originalBilledNetAmount !== undefined ? item.originalBilledNetAmount : item.value;
        const numericOrigValue = typeof origValue === "number" ? origValue : 0;

        let newBilledNetAmount;
        if (isAllZero) {
          newBilledNetAmount = equalShare;
        } else {
          const proportion = numericOrigValue / totalOriginalValue;
          newBilledNetAmount = proportion * group.changeTo;
        }

        return {
          ...item,
          billedNetAmount: parseFloat(newBilledNetAmount.toFixed(2)),
          originalBilledNetAmount: origValue,
        };
      });

      // Step 3: Adjust penny difference on the last record in the group
      const sumOfRoundedValues = updatedGroupRecords.reduce((sum, item) => sum + (item.billedNetAmount || 0), 0);
      const pennyDifference = parseFloat((group.changeTo - sumOfRoundedValues).toFixed(2));

      if (pennyDifference !== 0 && updatedGroupRecords.length > 0) {
        const lastIndex = updatedGroupRecords.length - 1;
        updatedGroupRecords[lastIndex] = {
          ...updatedGroupRecords[lastIndex],
          billedNetAmount: parseFloat((updatedGroupRecords[lastIndex].billedNetAmount + pennyDifference).toFixed(2)),
        };
      }

      updatedGroupRecords.forEach((updatedItem) => {
        const recordIndex = updatedData.findIndex((r) => r.timeId === updatedItem.timeId);
        if (recordIndex !== -1) {
          updatedData[recordIndex] = updatedItem;
        }
      });
    });

    // Step 4: Recalculate VAT based on new billedNetAmount
    if (vatvalue && vatvalue.rate) {
      const vatRate = vatvalue.rate / 100;
      updatedData = updatedData.map((item) => {
        if (!item.add) return item;
        const newVat = parseFloat(((item.billedNetAmount || 0) * vatRate).toFixed(2));
        return {
          ...item,
          billVat: newVat
        };
      });
    }

    // Step 5: Apply VAT penny adjustment
    const finalData = adjustVatPennyDifferences(updatedData);

    setData(finalData);
    setShowSplitDialog(false);
  };

  return (
    <Dialog title={`Time Record`} onClose={props.cancelEdit} className="dialog-xxl">
      <div className="d-flex flex-column h-100" >
        <div className="pt-4 pb-3 px-5">
          <div className="mb-2 d-flex justify-content-between align-items-center" style={{ color: "#262626" }}>
            <div className="d-flex align-items-center justify-content-center gap-2">
              <span className="fs-6">{summary.count} record{summary.count !== 1 ? "s" : ""} selected</span>
              {summary.count > 0 && (
                <button
                  type="button"
                  className="btn btn-link p-0 ms-3 fs-6 text-decoration-underline"
                  onClick={handleClearSelection}
                >
                  Clear Selection
                </button>
              )}
            </div>

            <div className="d-flex align-items-center">
              {[
                { label: "Total Units", value: AmountFormat(summary.totalUnits) },
                { label: "Bill Net", value: AmountFormat(summary.totalBillNet) },
                { label: "VAT", value: AmountFormat(summary.totalBillVat) },
                { label: "Total Bill", value: AmountFormat(summary.totalBill) },
              ].map(({ label, value }, index, items) => (
                <div
                  key={label}
                  className={`d-flex flex-column align-items-center px-3  ${index < items.length - 1 ? styles["numeric-right-border"] : ""}`}
                >
                  <div className={`${styles["font-small"]}`} style={{ fontSize: '0.875rem' }}>{label}</div>
                  <div className={`${styles["font-amount"]} mt-1`}>{value}</div>
                </div>
              ))}
            </div>
          </div>
          <div className={`p-4 ${styles["section-card"]}`}>

            <h5 className="fw-semibold" style={{ fontSize: '1rem' }}>Update Selected Records</h5>
            <div className="mb-3 border-bottom" />

            <div className="d-flex flex-wrap align-items-center validation">
              <Form
                ignoreModified={true}
                onSubmit={handleSubmit}
                id="timeRecord"
                initialValues={{
                  feeEarner: null
                }}
                render={(formRenderProps) => {
                  return (
                    <div style={{ width: "100%" }}>
                      <FormElement id="timeRecord" name="timeRecord">
                        <fieldset>
                          {formRenderProps.visited &&
                            formRenderProps.errors &&
                            formRenderProps.errors.VALIDATION_SUMMARY && (
                              <div className={"k-messagebox k-messagebox-error"}>
                                {formRenderProps.errors.VALIDATION_SUMMARY}
                              </div>
                            )}

                          <div className="d-flex flex-nowrap gap-3 align-items-stretch w-100">
                            <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                              <ValidatedComboBox
                                icon={LuTag}
                                label="VAT Code"
                                value={vatvalue}
                                name="vatCode"
                                data={activevatsdata}
                                textField="display"
                                filterable={true}
                                onFilterChange={filterChange}
                                iconClassName="wa"
                                onChange={onChangevat}
                                placeholder="Search VAT Code"
                                style={{ height: "32px" }}
                              />
                            </div>

                            <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                              <ValidatedComboBox
                                icon={FaRegBuilding}
                                label="Nominal Ref"
                                value={nominal}
                                showValidation={showValidation}
                                isRequired={data.some(item => item.add === true)}
                                name="nominalRef"
                                data={nominaldata}
                                iconClassName="wa"
                                textField="display"
                                filterable={true}
                                onFilterChange={filternominal}
                                onChange={onChangenominal}
                                placeholder="Search Nominal Ref"
                                loading={nominalsLoading}
                                style={{ height: "32px" }}
                              />
                            </div>

                            <div style={{ flex: "1 1 180px", minWidth: 0 }}>
                              <UpdateFieldCard icon={IoPersonOutline} label="Allocate To" className="h-100">
                                <div className={`w-100`}>
                                  <Field
                                    name="feeEarner"
                                    component={SearchComboBox}
                                    cbType={CONSTANT.FE}
                                    customLabel=" "
                                    // validator={requiredValidator}
                                    validationMessage=""
                                    iconClassName="wa"
                                    width="100%"
                                    style={{ height: "32px", width: "100%" }}
                                    placeholder="Search Fee Earner"
                                    customCombo={true}
                                    onChangeFunction={(e) => {
                                      let feeEarner = e.value;

                                      if (feeEarner && feeEarner.display) {
                                        const parts = feeEarner.display.split(' - ');
                                        const firstPart = parts[0]?.trim();

                                        feeEarner = {
                                          ...feeEarner,
                                          display: firstPart
                                        };
                                      }

                                      setSelectedFeeEarner(feeEarner);
                                      formRenderProps.onChange("feeEarner", { value: feeEarner });

                                      // Only apply to currently selected records if fee earner is selected
                                      if (data.length > 0 && feeEarner) {
                                        const updatedData = data.map((item) => {
                                          if (!item.add) return item;
                                          return { ...item, billFeeEarnerRef: feeEarner.userRef };
                                        });
                                        setData(updatedData);
                                      }
                                      // If fee earner is cleared (null), don't change anything - keep existing values

                                      if (company?.defaultProfitCostNominalType !== "User") {
                                        return;
                                      }

                                      const defaultRef = feeEarner?.defaultNominal;
                                      const match = defaultRef
                                        ? findNominalByRef(incomeNominals, defaultRef) ||
                                        findNominalByRef(allNominals, defaultRef) || {
                                          nominalRef: String(defaultRef),
                                          display: String(defaultRef),
                                        }
                                        : null;

                                      let selectedNominal = match;
                                      if (selectedNominal?.display) {
                                        const firstPart = selectedNominal.display.split(" - ")[0]?.trim();
                                        selectedNominal = {
                                          ...selectedNominal,
                                          display: firstPart,
                                        };
                                      }

                                      setnominal(selectedNominal);
                                      setData((prev) =>
                                        prev.map((item) =>
                                          item.add
                                            ? {
                                              ...item,
                                              nominalRef:
                                                selectedNominal?.nominalRef ||
                                                "",
                                            }
                                            : item,
                                        ),
                                      );
                                      if (selectedNominal) {
                                        setnominaldata((prev) =>
                                          prev?.some(
                                            (item) =>
                                              String(item.nominalRef) ===
                                              String(selectedNominal.nominalRef),
                                          )
                                            ? prev
                                            : [selectedNominal, ...(prev || [])],
                                        );
                                      }
                                    }}
                                  />
                                </div>
                              </UpdateFieldCard>
                            </div>

                            <div style={{ flex: "1.5 1 330px", minWidth: 0 }}>
                              <UpdateFieldCard icon={LiaCoinsSolid} label="Bill Net Amount">
                                <div className="d-flex align-items-center gap-2 w-100">
                                  <div className={`d-flex align-items-stretch flex-grow-1 ${styles["bill-net-input-group"]}`}>
                                    <NumericTextBox
                                      value={summaryBillTo}
                                      onChange={(e) => setSummaryBillTo(e.value || 0)}
                                      format="n2"
                                      spinners={false}
                                      min={0}
                                      className="px-2"
                                      style={{ height: "32px" }}
                                    />
                                    <Button
                                      type="button"
                                      className="bgBtnSecondary button-small"
                                      onClick={handleApplyBillTo}
                                      style={{ height: "32px" }}
                                      disabled={!summaryBillTo || summaryBillTo <= 0}
                                    >
                                      Apply
                                    </Button>
                                  </div>
                                  <Button
                                    type="button"
                                    className="common-btn button-small flex-shrink-0"
                                    onClick={handleWriteOff}
                                    style={{ height: "32px" }}
                                  >
                                    Write Off
                                  </Button>
                                  <Button
                                    type="button"
                                    className="bgBtnSecondary button-small"
                                    style={{ height: "32px", width: "72px" }}
                                    onClick={() => setShowSplitDialog(true)}
                                    disabled={getSelectedItemsCount() === 0 || summary.totalBillNet <= 0}
                                  >
                                    % Split
                                  </Button>
                                </div>
                              </UpdateFieldCard>
                            </div>
                          </div>
                        </fieldset>
                      </FormElement>
                    </div>
                  );
                }}
              />
            </div>
          </div>
        </div>

        <div className="flex-grow-1 px-5" style={{ minHeight: 0 }}>
          <Grid
            data={data}
            dataItemKey={"timeId"}
            onItemChange={itemChange}
            className="grid-header-sm grid-minwidth-col4-200 "
            style={{ height: "100%" }}
            selectable={{
              enabled: true,
              drag: false,
              cell: false,
              mode: "multiple",
            }}
            showLoader={isLoading}
            loader={
              <div style={{ height: "400px" }}>
                <GridLoader />
              </div>
            }
          >
            <GridNoRecords>
              {isLoading ? (
                <span></span>
              ) : (
                data && data.length === 0 && "No records available"
              )}
            </GridNoRecords>
            <Column
              field="feeEarnerRef"
              title="FE"
              editable={false}
              width={"60"}
              footerCell={TotalLabelFooterCell}
            />
            <Column
              field="billFeeEarnerRef"
              title="Bill FE"
              editable={false}
              width={"70"}
              footerCell={EmptyFooterCell}
            />
            <Column
              field="date"
              title="Date"
              editable={false}
              cell={DateFormatCell}
              width={"100"}
              footerCell={EmptyFooterCell}
            />

            <Column
              field="details"
              title="Details"
              editable={false}
              // width={"300"}
              cell={NameTextCell}
              footerCell={EmptyFooterCell}
            />
            <Column
              field="nominalRef"
              title="Nominal"
              editable={false}
              cell={(cellProps) => {
                return (
                  <td style={{ textAlign: "left" }}>
                    {cellProps.dataItem.nominalRef || ""}
                  </td>
                )
              }}
              width={"90"}
              footerCell={EmptyFooterCell}
            />
            <Column
              field="units"
              title="Units"
              editable={false}
              width={"63"}
              headerCell={AmountNameHeader}
              cell={(props) => (
                <td style={{ textAlign: "center" }}>
                  {props.dataItem.units}
                </td>
              )}
              footerCell={() => <TotalFooterCell>{overallTotals.totalUnits}</TotalFooterCell>}
            />
            <Column
              field="rate"
              title="Rate"
              editable={false}
              width={"60"}
              cell={AmountFormatCell}
              headerCell={AmountNameHeader}
              footerCell={EmptyFooterCell}
            />
            <Column
              field="value"
              title="Value"
              editable={false}
              width={"90"}
              cell={AmountFormatCell}
              headerCell={AmountNameHeader}
              footerCell={() => <TotalFooterCell>{overallTotals.totalValue.toFixed(2)}</TotalFooterCell>}
            />
            <Column
              field="billedNetAmount"
              title="Bill Net"
              editable={false}
              cell={BillNetCell}
              headerCell={AmountNameHeader}
              width={100}
              footerCell={EmptyFooterCell}
            />
            <Column
              field="billVat"
              width={"90"}
              title="Vat"
              editable={false}
              cell={AmountFormatCell}
              headerCell={AmountNameHeader}
              footerCell={EmptyFooterCell}
            />
            <Column
              field="add"
              title="Select"
              width={"90px"}
              headerCell={SelectionHeaderCell}
              footerCell={EmptyFooterCell}
              cell={(cellProps) => (
                <td className="text-center align-middle">
                  <Checkbox
                    style={{
                      width: "18px",
                      height: "18px",
                      boxShadow: "0px 0px 16px rgba(0, 0, 0, 0.1)",
                    }}
                    value={cellProps.dataItem.add === true}
                    onChange={(e) => {
                      itemChange({
                        dataItem: cellProps.dataItem,
                        value: e.value,
                      });
                    }}
                  />
                </td>
              )}
            />
          </Grid>
        </div>

        <div className="px-5 pb-3">
          {error === true && (
            <i style={{ color: "red" }}>
              {data.some(item => item.add === true && !item.nominalRef)
                ? "Nominal Ref is required for all selected records"
                : "Please fill in all required fields"}
            </i>
          )}
          {error1 === true && (getSelectedItemsCount() === 0 && data.length > 0) && (
            <i style={{ color: "red" }}>Select atleast 1 Record</i>
          )}
        </div>
      </div>
      <DialogActionsBar>

        <div className="dialog-footer">
          <button
            className="common-btn bgBtnSecondary border-0 me-3"
            onClick={() => {
              props.cancelEdit();
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            form="timeRecord"
            className="k-button common-btn me-4"
            disabled={data.filter(item => item.add === true).length === 0}
          >
            Add to Bill
          </button>
        </div>
      </DialogActionsBar>

      {showSplitDialog && (
        <PercentSplitDialog
          selectedRecords={data.filter(item => item.add === true)}
          totalBillNet={summary.totalBillNet}
          onCancel={() => setShowSplitDialog(false)}
          onUpdate={handleApplyPercentSplit}
        />
      )}

    </Dialog>
  );
};

export default MattersTimeRecords;
